<script setup lang="ts">
/**
 * The picture behind a page.
 *
 * Always the whole sheet, margins included, plus whatever is carried past the
 * trim: a background held inside the margins is a panel, not a background.
 * What differs is how it gets there, because it is drawn in two situations.
 *
 * **On a sheet** (`sheet`), on the canvas, where every page break opens a
 * sheet. The sheet is the page, so the picture is pinned to its top corner and
 * laid underneath everything on it, across the whole sheet, which is two pages
 * wide on a double page. The sheet isolates its own stacking, which is what lets
 * the picture sit below the text without a z-index reaching anything outside
 * the paper. Printed, the sheet starts the page, so the same rule is right on
 * paper.
 *
 * **In the flow** (`flow`), where sections are drawn without sheets around
 * them, so there is no sheet to pin to. The picture is a block exactly one page tall
 * whose bottom margin cancels its own height, so the text carries on as though
 * it were not there and is painted over it in tree order. On the canvas it
 * starts where the break is. Printed, the break has already ended the page,
 * so it starts at the top of the next one, inside that page's top margin, and
 * is pulled back out to the paper.
 *
 * Decorative in the strictest sense: it carries no information, and a reader
 * hearing the page described gains nothing from being told it is there.
 */
import { computed } from 'vue'

import { backgroundStyle, type PageBackground } from '@/domain/layout/background'

const props = defineProps<{
  background: PageBackground
  placement: 'sheet' | 'flow'
}>()

const style = computed(() => backgroundStyle(props.background))
</script>

<template>
  <!-- On a sheet, the picture sits in a frame exactly its size. The frame is
       what a view can shorten to crop the picture without scaling it: one
       continuous sheet shows each page as a stretch no taller than what is on
       it, and a picture a page tall would otherwise run past the stretch and
       over the desk beside the narrower page below a double page. -->
  <div v-if="placement === 'sheet'" class="backdrop-frame" aria-hidden="true">
    <div class="backdrop" :style="style" />
  </div>

  <div v-else class="backdrop backdrop--flow" :style="style" aria-hidden="true" />
</template>

<style scoped>
.backdrop {
  /* The sheet's own width, which is two pages across a double page. */
  width: calc(var(--sheet-width, var(--page-width)) + var(--page-bleed, 0mm) * 2);
  height: calc(var(--page-height) + var(--page-bleed, 0mm) * 2);

  background-image: var(--page-bg-image);
  background-size: var(--page-bg-size);
  background-repeat: var(--page-bg-repeat);
  background-position: center;
  opacity: var(--page-bg-strength, 1);

  /* Nothing is clickable behind the page: a selection that lands on the
     backdrop instead of the section over it is a section nobody can pick. */
  pointer-events: none;
}

.backdrop-frame {
  position: absolute;
  top: calc(var(--page-bleed, 0mm) * -1);
  left: calc(var(--page-bleed, 0mm) * -1);
  z-index: -1;
  width: calc(var(--sheet-width, var(--page-width)) + var(--page-bleed, 0mm) * 2);
  height: calc(var(--page-height) + var(--page-bleed, 0mm) * 2);
  overflow: hidden;
  pointer-events: none;
}

.backdrop--flow {
  /* Out to the paper edge on the left, since the page inset us by its margin. */
  margin-left: calc((var(--page-margin-side) + var(--page-bleed, 0mm)) * -1);
  margin-top: calc(var(--page-bleed, 0mm) * -1);

  /* Height plus top margin, negated: the flow ends where it began. */
  margin-bottom: calc((var(--page-height) + var(--page-bleed, 0mm)) * -1);
}

@media print {
  /* Pulled up by the page's top margin as well as the bleed. The bottom margin
     gives the margin back, or everything after the picture would print that
     much higher than it sits on the canvas. */
  .backdrop--flow {
    margin-top: calc((var(--page-margin-top) + var(--page-bleed, 0mm)) * -1);
    margin-bottom: calc(
      (var(--page-height) + var(--page-bleed, 0mm) - var(--page-margin-top)) * -1
    );
  }
}
</style>
